import { useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KEY = ["state"];

export function useBucket() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: KEY, queryFn: api.state });

  // Visited edits are applied instantly in the cache and synced to the server one at a time
  const queue = useRef(Promise.resolve());
  const pending = useRef(0);
  const findVisited = (country) => qc.getQueryData(KEY)?.visited.find((v) => v.country === country);
  const setVisited = (fn) => qc.setQueryData(KEY, (d) => ({ ...d, visited: fn(d.visited) }));

  const sync = (country, removedId) => {
    pending.current += 1;
    qc.cancelQueries({ queryKey: KEY });
    queue.current = queue.current
      .then(async () => {
        if (removedId) {
          const id = removedId.startsWith("tmp-") ? (await api.state()).visited.find((v) => v.country === country)?.id : removedId;
          if (id) await api.delVisited(id);
          return;
        }
        const cur = findVisited(country);
        if (!cur) return;
        const body = { cities: cur.cities, places: cur.places || [], trips: cur.trips || [], notes: cur.notes || "" };
        if (cur.id.startsWith("tmp-")) {
          const { data: saved } = await api.addVisited({ country, ...body });
          setVisited((list) => list.map((v) => (v.country === country ? { ...v, id: saved.id } : v)));
        } else {
          await api.patchVisited(cur.id, body);
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not save, refreshing"))
      .finally(() => {
        pending.current -= 1;
        if (pending.current === 0) qc.invalidateQueries({ queryKey: KEY });
      });
  };

  const newVisited = (country, cities = [], places = []) => ({ id: `tmp-${country}`, country, cities, places, trips: [], notes: "", created_at: "" });
  const sortByCountry = (list) => list.sort((a, c) => a.country.localeCompare(c.country));

  const fail = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  // Every change gets a toast with an Undo button
  const notify = (msg, undo) => toast.success(msg, { action: { label: "Undo", onClick: undo }, duration: 6000 });
  const undoRun = async (fn) => {
    try { await fn(); toast("Undone"); } catch (e) { fail(e); }
    refresh();
  };
  const act = async (fn, msg, makeUndo) => {
    try {
      const res = await fn();
      notify(msg, () => undoRun(() => makeUndo(res?.data)));
    } catch (e) { fail(e); }
    await refresh();
  };
  const current = () => qc.getQueryData(KEY);

  const restoreVisited = (country, prev) => {
    const cur = findVisited(country);
    if (prev) {
      const entry = { ...prev, id: cur ? cur.id : `tmp-${country}` };
      setVisited((list) => (cur ? list.map((x) => (x.country === country ? entry : x)) : sortByCountry([...list, entry])));
      sync(country);
    } else if (cur) {
      setVisited((list) => list.filter((x) => x.country !== country));
      sync(country, cur.id);
    }
    toast("Undone");
  };
  const changeVisited = (country, next, msg) => {
    const prev = findVisited(country);
    setVisited((list) => {
      const rest = list.filter((x) => x.country !== country);
      return next ? sortByCountry([...rest, next]) : rest;
    });
    sync(country, next ? undefined : prev?.id);
    notify(msg, () => restoreVisited(country, prev));
  };

  return {
    data,
    readOnly: false,
    toggleCountry: (name) => {
      const v = findVisited(name);
      changeVisited(name, v ? null : newVisited(name), v ? `${name} removed from visited` : `${name} marked as visited`);
    },
    toggleCity: (country, city, place) => {
      const v = findVisited(country);
      if (!v) return changeVisited(country, newVisited(country, [city], place ? [place] : []), `Been to ${city}, ${country}`);
      const has = v.cities.includes(city);
      const kept = (v.places || []).filter((p) => p.name !== city);
      const next = { ...v, cities: has ? v.cities.filter((c) => c !== city) : [...v.cities, city], places: has || !place ? kept : [...kept, place] };
      changeVisited(country, next, has ? `${city} unmarked` : `Been to ${city}`);
    },
    patchVisited: (id, b, msg) => {
      const v = current().visited.find((x) => x.id === id);
      if (v) changeVisited(v.country, { ...v, ...b }, msg || "Saved");
    },
    addWish: (name) => act(() => api.addWish({ country: name }), `${name} added to your bucketlist`, (w) => api.delWish(w.id)),
    // Favourite a city/island: the country goes on the bucketlist with that place in it
    wishPlace: (country, place) => {
      const w = current().wishlist.find((x) => x.country === country);
      const pin = { name: place.name, x: place.x, y: place.y };
      if (!w) return act(() => api.addWish({ country, cities: [place.name], places: [pin] }), `${place.name}, ${country} added to your bucketlist`, (n) => api.delWish(n.id));
      if ((w.cities || []).includes(place.name)) return toast(`${place.name} is already on your bucketlist`);
      const old = { cities: w.cities || [], places: w.places || [] };
      return act(() => api.patchWish(w.id, { cities: [...old.cities, place.name], places: [...old.places.filter((p) => p.name !== place.name), pin] }),
        `${place.name} added to ${country}`, () => api.patchWish(w.id, old));
    },
    patchWish: (id, b, msg) => {
      const prev = current().wishlist.find((w) => w.id === id);
      const old = Object.fromEntries(Object.keys(b).map((k) => [k, prev?.[k]]));
      return act(() => api.patchWish(id, b), msg || "Saved", () => api.patchWish(id, old));
    },
    delWish: (id) => {
      const list = current().wishlist;
      const prev = list.find((w) => w.id === id);
      return act(() => api.delWish(id), `${prev?.country || "Country"} removed from bucketlist`, async () => {
        const { data: w } = await api.addWish({ country: prev.country, target_date: prev.target_date, notes: prev.notes, contacts: prev.contacts, cities: prev.cities || [], places: prev.places || [] });
        await api.reorder(list.map((x) => (x.id === id ? w.id : x.id)));
      });
    },
    reorder: (list) => {
      const prev = current().wishlist;
      qc.setQueryData(KEY, (d) => ({ ...d, wishlist: list }));
      return act(() => api.reorder(list.map((w) => w.id)), "Order changed", () => api.reorder(prev.map((w) => w.id)));
    },
    reorderGoals: (list) => {
      const prev = current().goals;
      qc.setQueryData(KEY, (d) => ({ ...d, goals: list }));
      return act(() => api.reorderGoals(list.map((g) => g.id)), "Order changed", () => api.reorderGoals(prev.map((g) => g.id)));
    },
    addGoal: (b) => act(() => api.addGoal(b), "Added to your life list", (g) => api.delGoal(g.id)),
    setTop5: (items, msg) => {
      const prev = current().top5 || [];
      qc.setQueryData(KEY, (d) => ({ ...d, top5: items }));
      return act(() => api.setTop5(items), msg || "Top 5 updated", () => api.setTop5(prev));
    },
    patchGoal: (id, b, msg) => {
      const prev = current().goals.find((g) => g.id === id);
      const old = Object.fromEntries([...Object.keys(b), "completed_at"].map((k) => [k, prev?.[k] ?? ""]));
      return act(() => api.patchGoal(id, b), msg || (b.done === false ? "Marked as not done" : "Goal updated"), () => api.patchGoal(id, old));
    },
    delGoal: (id) => {
      const g = current().goals.find((x) => x.id === id);
      const { id: _id, ...rest } = g;
      return act(() => api.delGoal(id), "Goal removed", () => api.addGoal(rest));
    },
  };
}
